<template>
  <div class="dropdown icon-perfil-container">
    <button
      id="userDropdown"
      class="icon-perfil"
      :class="isUsuarioLogged ? 'border border-secondary' : ''"
      type="button"
      aria-expanded="false"
      @click="toggleDropdown">
      <span v-if="isUsuarioLogged" class="text-secondary p bold rounded-circle">
        {{ nombreUsuario[0].toUpperCase() }}
      </span>
      <CustomIcon v-else-if="isLanding" :icon="userIcon" size="20px" class="landing-avatar-icon" />
      <i v-else class="fa-regular fa-user fa-lg" />
    </button>

    <ul id="userDropMenu" class="dropdown-menu dropdown-menu-end p-3 shadow-lg" aria-labelledby="userDropdown">
      <div>
        <li>
          <p class="text-primary bold border-bottom-2 px-3 py-1 h6 item-title mb-10">
            <u>
              {{ $t('header.user.title') }}
            </u>
          </p>
        </li>
        <template v-if="!isUsuarioLogged">
          <li>
            <span class="dropdown-item" @click="openModalLogin()">{{ $t('header.user.login') }}</span>
          </li>
          <li>
            <span class="dropdown-item" @click="openModalLogin()">{{ $t('header.user.signIn') }}</span>
          </li>
        </template>

        <template v-else>
          <li>
            <router-link class="dropdown-item" :to="{ name: UsuarioProximasReservas }">
              {{ $t('reserva.misReservas') }}
            </router-link>
          </li>
          <li v-if="hasRoleGestorRooming">
            <router-link class="dropdown-item" :to="{ name: UsuarioRooming }">
              {{ $t('rooming.miRooming') }}
            </router-link>
          </li>
          <li>
            <router-link class="dropdown-item" :to="{ name: 'perfil' }">
              {{ $t('general.miPerfil') }}
            </router-link>
          </li>
          <li v-if="hasRoleComercial">
            <router-link class="dropdown-item" :to="{ name: 'AreaComercial' }">
              {{ $t('subtitulo.areaComercial') }}
            </router-link>
          </li>
          <li>
            <button class="dropdown-item" @click="botonLogout()">
              {{ $t('general.cerrarSesion') }}
            </button>
          </li>
        </template>
      </div>
    </ul>
  </div>
</template>

<script setup>
  import { UsuarioProximasReservas, UsuarioRooming } from '@/router/RouterNames';
  import { computed, onMounted } from 'vue';
  import { useLoginStore } from '@/store_pinia/login';
  import { createToaster } from '@meforma/vue-toaster';
  import { useI18n } from 'vue-i18n';
  import router from '@/router';
  import { useUserStore } from '@/store_pinia/userStore';
  import CustomIcon from '@/components/CustomIcon.vue';
  import userIcon from '@/assets/icons/user.svg';
  import { GruposInformacionViaje, AgrupacionDetalle } from '@/router/RouterNames';
  import { isViajeExperienciaStyleRoute } from '@/helpers/landingStyleUtils';

  const userStore = useUserStore();
  const loginStore = useLoginStore();
  const toaster = createToaster({ position: 'top-right' });
  const { t } = useI18n();

  const isLanding = computed(
    () =>
      [GruposInformacionViaje, AgrupacionDetalle].includes(router.currentRoute.value.name) ||
      isViajeExperienciaStyleRoute(router.currentRoute.value.name)
  );

  const isUsuarioLogged = computed(() => userStore.isLogged);
  const nombreUsuario = computed(() => userStore.getUsuario.nombre);
  const hasRoleGestorRooming = computed(() => userStore.hasRoleGestorRooming);
  const hasRoleComercial = computed(() => userStore.hasRoleComercial);

  function openModalLogin() {
    loginStore.openLoginModal();
  }

  function botonLogout() {
    userStore.logout();
    toaster.success(t('header.login.msg.logoutSuccess'));
    if (router.currentRoute.value.matched.some(r => r.meta.requiresLogin)) {
      router.push({ name: 'home' });
    }
  }

  function toggleDropdown() {
    const dropdownMenu = document.getElementById('userDropMenu');
    if (dropdownMenu) {
      if (dropdownMenu.classList.contains('show')) {
        dropdownMenu.classList.remove('show');
      } else {
        dropdownMenu.classList.add('show');
      }
    }
  }

  function handleClickOutside(event) {
    const dropdownMenu = document.getElementById('userDropMenu');
    const dropdownButton = document.getElementById('userDropdown');

    if (
      dropdownMenu &&
      dropdownMenu.classList.contains('show') &&
      !dropdownMenu.contains(event.target) &&
      !dropdownButton.contains(event.target)
    ) {
      dropdownMenu.classList.remove('show');
    }
  }

  function closeDropdownOnRouteChange() {
    const dropdownMenu = document.getElementById('userDropMenu');
    if (dropdownMenu && dropdownMenu.classList.contains('show')) {
      dropdownMenu.classList.remove('show');
    }
  }

  onMounted(function () {
    document.addEventListener('click', handleClickOutside);
    router.afterEach(closeDropdownOnRouteChange);
  });
</script>

<style scoped lang="scss">
  .dropdown-item {
    cursor: pointer;
    transition: 0.2s;
  }

  .dropdown-item:hover {
    background-color: transparent;
    color: var(--bs-secondary);
  }

  .icon-perfil-container {
    z-index: 999;
    width: 60px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 2px;
    position: relative;

    @media (max-width: 768px) {
      width: 35px;
      height: 35px;
    }
  }

  .icon-perfil {
    z-index: 1002;
    width: 45px;
    height: 45px;
    background-color: var(--bs-primary);
    border: 1.5px solid var(--bs-secondary);
    border-radius: 50%;
    color: var(--bs-secondary);
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 20px;
    box-shadow: 0 1px 3px 0 rgb(var(--bs-primary-rgb), 0.5);
    cursor: pointer;

    @media (max-width: 768px) {
      width: 35px;
      height: 35px;
      font-size: 15px;
    }
  }

  .dropdown-menu {
    position: absolute;
    right: 0;
    left: auto;
    top: 100%;
    margin-top: 5px;
    min-width: 200px;
    transform: none !important;
  }

  .item-title {
    text-underline-offset: 8px;
  }

  .landing-avatar-icon {
    color: var(--landing-header-detail-color);
  }
  .header-landing .icon-perfil {
    background-color: transparent !important;
    border-color: var(--landing-header-detail-color, var(--bs-secondary)) !important;
  }

  .header-landing .icon-perfil .text-secondary {
    color: var(--landing-header-detail-color, var(--bs-secondary)) !important;
  }
</style>
